import { Type, type Static } from "@sinclair/typebox";
import type { ReportChange } from "./technical-reports.js";
import type { PersonalAgendaChange } from "./personal-agenda.js";
import type { AssistantOperationsChange } from "./assistant-operations.js";
export * from "./assistant-operations.js";

export const AssistantScopes = [
  "reports:read",
  "reports:write",
  "reports:admin",
  "jobs:read",
  "jobs:write",
  "clients:read",
  "clients:write",
  "quotes:read",
  "lifelines:read",
  "lifelines:write",
  "planning:read",
  "planning:write",
  "agenda:read",
  "agenda:write",
  "administration:read",
  "employees:read",
  "archive:read",
] as const;
export type AssistantScope = (typeof AssistantScopes)[number];
const scope = Type.Union(AssistantScopes.map((value) => Type.Literal(value)));
const uuid = Type.String({ format: "uuid" });
const nullableText = (maxLength: number) =>
  Type.Unsafe<string | null>({ type: ["string", "null"], maxLength });
const nullableDate = Type.Unsafe<string | null>({
  type: ["string", "null"],
  format: "date",
});
const strict = { additionalProperties: false } as const;
export const AssistantConnectionDurationDays = {
  minimum: 1,
  maximum: 365,
  default: 365,
} as const;
export const AssistantConnectionCreateSchema = Type.Object(
  {
    operationId: uuid,
    name: Type.String({ minLength: 1, maxLength: 80, pattern: "\\S" }),
    scopes: Type.Array(scope, {
      minItems: 1,
      maxItems: AssistantScopes.length,
      uniqueItems: true,
    }),
    expiresInDays: Type.Integer(AssistantConnectionDurationDays),
  },
  strict,
);
export type AssistantConnectionCreate = Static<
  typeof AssistantConnectionCreateSchema
>;
export const AssistantConnectionReconnectSchema = Type.Object(
  { operationId: uuid },
  strict,
);
export const AssistantConnectionScopesSchema = Type.Object(
  {
    operationId: uuid,
    expectedScopes: Type.Array(Type.String({ minLength: 1, maxLength: 80 }), {
      maxItems: 100,
      uniqueItems: true,
    }),
    scopes: AssistantConnectionCreateSchema.properties.scopes,
  },
  strict,
);
export type AssistantConnectionScopes = Static<
  typeof AssistantConnectionScopesSchema
>;
export const AssistantYearSchema = Type.Object(
  { managementYear: Type.Integer({ minimum: 2000, maximum: 2200 }) },
  strict,
);
export const AssistantYearPageSchema = Type.Object(
  {
    ...AssistantYearSchema.properties,
    offset: Type.Optional(
      Type.Integer({ minimum: 0, maximum: 100000, default: 0 }),
    ),
    limit: Type.Optional(
      Type.Integer({ minimum: 1, maximum: 25, default: 10 }),
    ),
  },
  strict,
);
export const AssistantInternalCostsSchema = Type.Object(
  {
    ...AssistantYearPageSchema.properties,
    section: Type.Optional(
      Type.Union([Type.Literal("centers"), Type.Literal("unassigned-jobs")], {
        default: "centers",
      }),
    ),
  },
  strict,
);
export type AssistantYearPage = Static<typeof AssistantYearPageSchema>;
export type AssistantInternalCostsQuery = Static<
  typeof AssistantInternalCostsSchema
>;
export const AssistantJobChangesSchema = Type.Object(
  {
    title: Type.Optional(
      Type.String({ minLength: 1, maxLength: 240, pattern: "\\S" }),
    ),
    description: Type.Optional(nullableText(4000)),
    informationNotes: Type.Optional(nullableText(20000)),
    contactPhone: Type.Optional(nullableText(80)),
    expectedEndOn: Type.Optional(nullableDate),
  },
  { ...strict, minProperties: 1 },
);
export const AssistantClientChangesSchema = Type.Object(
  {
    email: Type.Optional(
      Type.Unsafe<string | null>({
        type: ["string", "null"],
        format: "email",
        maxLength: 254,
      }),
    ),
    phone: Type.Optional(nullableText(80)),
  },
  { ...strict, minProperties: 1 },
);
export const AssistantChangeSchema = Type.Union([
  Type.Object(
    {
      kind: Type.Literal("job.update"),
      id: uuid,
      version: Type.Integer({ minimum: 1 }),
      changes: AssistantJobChangesSchema,
    },
    strict,
  ),
  Type.Object(
    {
      kind: Type.Literal("client.update"),
      id: uuid,
      version: Type.Integer({ minimum: 1 }),
      changes: AssistantClientChangesSchema,
    },
    strict,
  ),
]);
export type AssistantChange =
  | Static<typeof AssistantChangeSchema>
  | AssistantOperationsChange
  | PersonalAgendaChange
  | ReportChange;
export const AssistantExecuteSchema = Type.Object(
  {
    proposal: Type.String({ minLength: 1, maxLength: 60000 }),
    confirmed: Type.Literal(true),
  },
  strict,
);
export const AssistantSearchSchema = Type.Object(
  {
    search: Type.Optional(Type.String({ maxLength: 200 })),
    cursor: Type.Optional(Type.String({ minLength: 1, maxLength: 512 })),
    limit: Type.Optional(
      Type.Integer({ minimum: 1, maximum: 25, default: 10 }),
    ),
  },
  strict,
);
export const AssistantJobSearchSchema = Type.Object(
  {
    ...AssistantSearchSchema.properties,
    code: Type.Optional(Type.String({ maxLength: 80 })),
    status: Type.Optional(Type.String({ maxLength: 40 })),
    managementYear: Type.Integer({ minimum: 2000, maximum: 2200 }),
  },
  strict,
);
export const AssistantQuoteSearchSchema = Type.Object(
  {
    search: Type.Optional(Type.String({ maxLength: 200 })),
    managementYear: Type.Integer({ minimum: 2000, maximum: 2200 }),
    limit: Type.Optional(
      Type.Integer({ minimum: 1, maximum: 25, default: 10 }),
    ),
  },
  strict,
);
export const AssistantIdSchema = Type.Object({ id: uuid }, strict);
